import React, { useState, useCallback, useEffect } from 'react';
import type { CellContextMenuEvent, ColDef } from 'ag-grid-community';

/**
 * AG-Grid 공통 복사 유틸.
 * 우클릭 시 "셀 복사 / 행 복사" 컨텍스트 메뉴 제공.
 *
 * 사용법:
 *   const copyMenu = useGridCopyMenu();
 *   <AgGridReact {...copyMenu.gridProps} ... />
 *   {copyMenu.menu}
 *
 * 내부적으로 navigator.clipboard 가 없거나 HTTPS 아닌 환경에서는 textarea+execCommand fallback 사용.
 */

export function copyTextToClipboard(text: string) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    return;
  }
  fallbackCopy(text);
}

function fallbackCopy(text: string) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  } catch (e) {
    console.error('clipboard copy failed', e);
  }
}

interface GridContextMenuState {
  x: number;
  y: number;
  cellValue: string;
  rowText: string;
  colHeader: string;   // 우클릭한 컬럼의 헤더명
  rowCount: number;    // 전체 복사 대상 행 수
  copyAll: (withHeader: boolean) => string;
  selectAll: () => void;
}

/**
 * 행(row)의 모든 컬럼을 탭 구분자로 직렬화.
 * 숨김 컬럼 제외, 표시되는 valueFormatter 결과를 우선 사용.
 * excludeFields: 복사에서 제외할 field 이름 목록
 */
function serializeRow(ev: CellContextMenuEvent, excludeFields?: string[]): string {
  const api = ev.api;
  const node = ev.node;
  if (!node) return '';
  const cols = api.getAllDisplayedColumns();
  const parts: string[] = [];
  for (const col of cols) {
    const colDef = col.getColDef() as ColDef;
    // 체크박스, 선택 컬럼 등 데이터 없는 컬럼 스킵
    if (!colDef || (colDef.headerName === '' && !colDef.field && !colDef.valueGetter)) continue;
    // 제외 필드 스킵
    if (excludeFields && colDef.field && excludeFields.includes(colDef.field)) continue;
    let val: any;
    try {
      val = api.getCellValue({ rowNode: node, colKey: col, useFormatter: true } as any);
    } catch {
      val = node.data?.[colDef.field ?? ''] ?? '';
    }
    parts.push(val == null ? '' : String(val));
  }
  return parts.join('\t');
}

/** 복사 대상 컬럼 목록 (숨김/체크박스/제외 필드 제거) */
function copyCols(api: CellContextMenuEvent['api'], excludeFields?: string[]) {
  return api.getAllDisplayedColumns().filter(col => {
    const colDef = col.getColDef() as ColDef;
    if (!colDef || (colDef.headerName === '' && !colDef.field && !colDef.valueGetter)) return false;
    if (excludeFields && colDef.field && excludeFields.includes(colDef.field)) return false;
    return true;
  });
}

/** 헤더 행을 탭 구분자로 직렬화 */
function serializeHeader(ev: CellContextMenuEvent, excludeFields?: string[]): string {
  return copyCols(ev.api, excludeFields)
    .map(col => {
      const colDef = col.getColDef() as ColDef;
      return colDef.headerName ?? colDef.field ?? '';
    })
    .join('\t');
}

/**
 * 화면에 보이는(정렬·필터 반영) 전체 행을 탭 구분자로 직렬화.
 * withHeader=true 면 첫 줄에 컬럼 헤더를 붙인다.
 */
function serializeAll(ev: CellContextMenuEvent, excludeFields?: string[], withHeader = true): string {
  const api = ev.api;
  const cols = copyCols(api, excludeFields);
  const lines: string[] = [];
  if (withHeader) lines.push(serializeHeader(ev, excludeFields));
  api.forEachNodeAfterFilterAndSort(node => {
    if (!node.data) return;   // 그룹/집계 행 스킵
    const parts = cols.map(col => {
      let val: any;
      try {
        val = api.getCellValue({ rowNode: node, colKey: col, useFormatter: true } as any);
      } catch {
        val = node.data?.[(col.getColDef() as ColDef).field ?? ''] ?? '';
      }
      return val == null ? '' : String(val);
    });
    lines.push(parts.join('\t'));
  });
  return lines.join('\n');
}

/** 화면에 보이는 행 수 (필터 반영) */
function visibleRowCount(api: CellContextMenuEvent['api']): number {
  let n = 0;
  api.forEachNodeAfterFilterAndSort(node => { if (node.data) n++; });
  return n;
}

function getCellText(ev: CellContextMenuEvent): string {
  const { value } = ev;
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** 컨텍스트 메뉴 항목 */
const MenuItem: React.FC<{
  label: string;
  hint?: string;
  strong?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, hint, strong, disabled, onClick }) => (
  <div
    onClick={() => { if (!disabled) onClick(); }}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      padding: '7px 12px', cursor: disabled ? 'default' : 'pointer',
      color: disabled ? '#9ca3af' : '#1f2937',
      fontWeight: strong ? 600 : 400,
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.backgroundColor = '#eff6ff'; }}
    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
  >
    <span>{label}</span>
    {hint && (
      <span style={{
        fontSize: '10px', color: disabled ? '#d1d5db' : '#6b7280',
        backgroundColor: disabled ? 'transparent' : '#f1f5f9',
        borderRadius: '3px', padding: '1px 5px', whiteSpace: 'nowrap',
      }}>{hint}</span>
    )}
  </div>
);

interface GridCopyMenuOptions {
  excludeFields?: string[];  // 셀 복사 및 행 복사에서 제외할 field 이름
  excludeFieldsFromCellCopy?: string[];  // 셀 복사에서만 제외 (기본: excludeFields 사용)
}

export function useGridCopyMenu(options?: GridCopyMenuOptions) {
  const [menu, setMenu] = useState<GridContextMenuState | null>(null);
  const excludeFields = options?.excludeFields ?? [];
  const excludeFieldsFromCellCopy = options?.excludeFieldsFromCellCopy ?? excludeFields;

  const onCellContextMenu = useCallback((ev: CellContextMenuEvent) => {
    const me = ev.event as MouseEvent | undefined;
    if (!me) return;
    me.preventDefault();
    const colDef = ev.column?.getColDef() as ColDef | undefined;
    const colField = colDef?.field ?? '';
    const isExcludedCell = excludeFieldsFromCellCopy.includes(colField);
    setMenu({
      x: me.clientX,
      y: me.clientY,
      cellValue: isExcludedCell ? '' : getCellText(ev),
      rowText: serializeRow(ev, excludeFields),
      colHeader: colDef?.headerName ?? colField,
      rowCount: visibleRowCount(ev.api),
      copyAll: (withHeader: boolean) => serializeAll(ev, excludeFields, withHeader),
      selectAll: () => { try { ev.api.selectAll(); } catch { /* 선택 미지원 그리드 무시 */ } },
    });
  }, [excludeFields, excludeFieldsFromCellCopy]);

  const close = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (!menu) return;
    const onDown = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const gridProps = { onCellContextMenu, preventDefaultOnContextMenu: true } as const;

  const menuNode = menu ? (
    <div
      onMouseDown={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: Math.min(menu.y, window.innerHeight - 190),
        left: Math.min(menu.x, window.innerWidth - 220),
        zIndex: 10000,
        backgroundColor: 'white',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(30,64,175,0.18)',
        minWidth: '200px',
        fontSize: '12px',
        userSelect: 'none',
        overflow: 'hidden',
        padding: '4px 0',
      }}
    >
      {/* 헤더 — 우클릭한 컬럼 표시 */}
      <div style={{
        padding: '6px 12px 7px', margin: '-4px 0 4px',
        backgroundColor: '#eff6ff', borderBottom: '1px solid #dbeafe',
        color: '#1e40af', fontWeight: 700, fontSize: '11px',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {menu.colHeader || '그리드'} · {menu.rowCount.toLocaleString()}행
      </div>

      <MenuItem
        label="셀 복사"
        hint={menu.cellValue ? undefined : '제외'}
        disabled={!menu.cellValue}
        onClick={() => { copyTextToClipboard(menu.cellValue); close(); }}
      />
      <MenuItem
        label="행 복사"
        onClick={() => { copyTextToClipboard(menu.rowText); close(); }}
      />

      <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0' }} />

      <MenuItem
        label="전체 선택"
        onClick={() => { menu.selectAll(); close(); }}
      />
      <MenuItem
        label="전체 복사"
        hint="헤더 포함"
        strong
        onClick={() => { copyTextToClipboard(menu.copyAll(true)); close(); }}
      />
      <MenuItem
        label="전체 복사"
        hint="값만"
        onClick={() => { copyTextToClipboard(menu.copyAll(false)); close(); }}
      />
    </div>
  ) : null;

  return { gridProps, menu: menuNode };
}
